import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { RadioGroup } from '@bb-ui/react-library/dist/components/RadioGroup';
import { Radio } from '@bb-ui/react-library/dist/components/Radio';
import { FormControlLabel } from '@bb-ui/react-library/dist/components/FormControlLabel';
import { useSpMetadataUrl } from '../spMetadataUrl';
import {
    GUIDED_TYPE_SUFFIX,
    IdpGuidanceType,
} from '../identityProviderManagement.types';
import { useStyles } from '../Authentication.styles';
import { TransText } from '../transHelper';

const GUIDED_TYPES: IdpGuidanceType[] = [
    'ENTRA',
    'OKTA',
    'SHIBBOLETH',
    'OTHER',
];

interface Props {
    /** Which provider's instructions to show, or which to start on when selectable. */
    type: IdpGuidanceType;
    /**
     * Let the reader pick the system the guide is for.
     */
    selectable?: boolean;
    /**
     * The Auth0 connection the metadata URL points at. Undefined until it is
     * known — right after an add, that is only once provisioning reports it.
     */
    connectionName?: string;
}

/**
 * What the admin does in their own identity provider console, plus the value
 * they paste there. Shared by the Add wizard and the Edit dialog rather than
 * copied into both, so the two cannot drift apart.
 */
export const SetupGuide: React.FunctionComponent<Props> = ({
    type,
    selectable = false,
    connectionName,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const spMetadataUrl = useSpMetadataUrl(connectionName);

    const [chosen, setChosen] = React.useState<IdpGuidanceType>(type);
    const shown = selectable ? chosen : type;

    const typeName = t(
        `settings.authentication.wizard.type${GUIDED_TYPE_SUFFIX[shown]}`,
    );

    const steps: string[] = String(
        t(`settings.authentication.wizard.guide${GUIDED_TYPE_SUFFIX[shown]}`),
    ).split('|');

    return (
        <div className={classes.guideCard} data-testid="wizardSetupGuide">
            {selectable && (
                <RadioGroup
                    row
                    legendText={t(
                        'settings.authentication.wizard.guideTypeLegend',
                    )}
                    value={chosen}
                    onChange={(_, v) => setChosen(v as IdpGuidanceType)}
                    className={classes.guideTypeChoices}
                >
                    {GUIDED_TYPES.map(option => (
                        <FormControlLabel
                            key={option}
                            value={option}
                            control={<Radio />}
                            data-testid={`guideType-${option}`}
                            label={
                                <TransText
                                    text={t(
                                        `settings.authentication.wizard.type${GUIDED_TYPE_SUFFIX[option]}`,
                                    )}
                                    tags={[<span key="english" lang="en" />]}
                                />
                            }
                        />
                    ))}
                </RadioGroup>
            )}
            <Typography variant="subtitle2" component="h4">
                <TransText
                    text={t('settings.authentication.wizard.guideTitle', {
                        name: typeName,
                    })}
                    tags={[<span key="english" lang="en" />]}
                />
            </Typography>
            <Typography variant="body2" className={classes.guideLead}>
                {t('settings.authentication.wizard.guideLead')}
            </Typography>
            <ol className={classes.guideSteps}>
                {steps.map(line => (
                    <li key={line}>
                        <TransText
                            text={line}
                            tags={[<span key="english" lang="en" />]}
                        />
                    </li>
                ))}
            </ol>
            <div className={classes.spSection}>
                <Typography variant="subtitle2" component="h4">
                    {t('settings.authentication.wizard.spHeading')}
                </Typography>
                {spMetadataUrl ? (
                    <dl className={classes.definitionList}>
                        <div
                            className={classnames(
                                classes.definitionRow,
                                classes.definitionRowPlain,
                            )}
                        >
                            <dt className={classes.definitionTerm}>
                                {t(
                                    'settings.authentication.wizard.spMetadataUrl',
                                )}
                            </dt>
                            <dd
                                className={classnames(
                                    classes.definitionValue,
                                    classes.monoValue,
                                )}
                                data-testid="spMetadataUrl"
                            >
                                {spMetadataUrl}
                            </dd>
                        </div>
                    </dl>
                ) : (
                    <Typography variant="body2" data-testid="spUnavailable">
                        {t('settings.authentication.wizard.spUnavailable')}
                    </Typography>
                )}
            </div>
        </div>
    );
};
